import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Typography,
  Box,
  Divider,
  Alert,
  CircularProgress,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getBulkMilestoneCount } from "../../services/userServices";

// ─── Constants ────────────────────────────────────────────────────────────────

const STEPS = { SELECT: 0, CONFIRM: 1 };

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatCount = (n) => (n ?? 0).toLocaleString();

const buildWarningText = (
  milestoneName,
  applyRosters,
  applyFrameworks,
  counts,
) => {
  const parts = [];
  if (applyFrameworks)
    parts.push(`${formatCount(counts.activeFrameworkCount)} Active Frameworks`);
  if (applyRosters)
    parts.push(`${formatCount(counts.activeRosterCount)} Active Rosters`);
  return `WARNING: Milestone "${milestoneName}" will be applied to ${parts.join(" and ")}.`;
};

// ─── Step 1: Target Selection ─────────────────────────────────────────────────

const SelectionStep = ({ applyRosters, applyFrameworks, onChange }) => (
  <Box>
    <Typography
      sx={{
        fontSize: "13px",
        color: "#6B7B8A",
        fontFamily: "Urbanist, sans-serif",
        mb: 2,
      }}
    >
      Select where this milestone should be applied:
    </Typography>

    <Box
      onClick={() => onChange("rosters", !applyRosters)}
      sx={{
        display: "flex",
        alignItems: "center",
        p: "10px 14px",
        mb: 1,
        border: applyRosters ? "1px solid #3299FF" : "1px solid #E4E8ED",
        borderRadius: "8px",
        cursor: "pointer",
        backgroundColor: applyRosters ? "#F0F7FF" : "transparent",
        transition: "all 0.15s",
        "&:hover": { backgroundColor: applyRosters ? "#F0F7FF" : "#F8FAFB" },
      }}
    >
      <Checkbox
        checked={applyRosters}
        onChange={(e) => onChange("rosters", e.target.checked)}
        onClick={(e) => e.stopPropagation()}
        size="small"
        sx={{
          color: "#B0BEC5",
          "&.Mui-checked": { color: "#3299FF" },
          p: "2px",
          mr: 1.5,
        }}
      />
      <Box>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            fontFamily: "Urbanist, sans-serif",
            color: "#3F5765",
          }}
        >
          Apply to All Active Rosters
        </Typography>
        <Typography
          sx={{
            fontSize: "12px",
            color: "#8A9BB0",
            fontFamily: "Urbanist, sans-serif",
          }}
        >
          Adds this milestone to every currently active roster
        </Typography>
      </Box>
    </Box>

    <Box
      onClick={() => onChange("frameworks", !applyFrameworks)}
      sx={{
        display: "flex",
        alignItems: "center",
        p: "10px 14px",
        border: applyFrameworks ? "1px solid #3299FF" : "1px solid #E4E8ED",
        borderRadius: "8px",
        cursor: "pointer",
        backgroundColor: applyFrameworks ? "#F0F7FF" : "transparent",
        transition: "all 0.15s",
        "&:hover": {
          backgroundColor: applyFrameworks ? "#F0F7FF" : "#F8FAFB",
        },
      }}
    >
      <Checkbox
        checked={applyFrameworks}
        onChange={(e) => onChange("frameworks", e.target.checked)}
        onClick={(e) => e.stopPropagation()}
        size="small"
        sx={{
          color: "#B0BEC5",
          "&.Mui-checked": { color: "#3299FF" },
          p: "2px",
          mr: 1.5,
        }}
      />
      <Box>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            fontFamily: "Urbanist, sans-serif",
            color: "#3F5765",
          }}
        >
          Apply to All Active Frameworks
        </Typography>
        <Typography
          sx={{
            fontSize: "12px",
            color: "#8A9BB0",
            fontFamily: "Urbanist, sans-serif",
          }}
        >
          Adds this milestone to every currently active framework
        </Typography>
      </Box>
    </Box>
  </Box>
);

// ─── Step 2: Already Assigned ────────────────────────────────────────────────

const AlreadyAssignedStep = ({ applyRosters, applyFrameworks }) => {
  const buildAlreadyMessage = () => {
    if (applyRosters && applyFrameworks)
      return "This milestone is already assigned to all Active Rosters and Active Frameworks.";
    if (applyRosters)
      return "This milestone is already assigned to all Active Rosters.";
    return "This milestone is already assigned to all Active Frameworks.";
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        py: 2,
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          backgroundColor: "#EAF7EE",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 28, color: "#2AA154" }} />
      </Box>
      <Typography
        sx={{
          fontSize: "15px",
          fontWeight: "bold",
          fontFamily: "Urbanist, sans-serif",
          color: "#3F5765",
        }}
      >
        Already Assigned
      </Typography>
      <Typography
        sx={{
          fontSize: "13px",
          fontFamily: "Urbanist, sans-serif",
          color: "#6B7B8A",
          lineHeight: 1.6,
        }}
      >
        {buildAlreadyMessage()}
      </Typography>
    </Box>
  );
};

// ─── Step 2: Warning + Confirm ────────────────────────────────────────────────

const ConfirmStep = ({
  milestoneName,
  applyRosters,
  applyFrameworks,
  confirmed,
  onConfirmChange,
  counts,
  countsLoading,
}) => {
  const warningText = buildWarningText(
    milestoneName,
    applyRosters,
    applyFrameworks,
    counts,
  );

  return (
    <Box>
      {countsLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} sx={{ color: "#3299FF" }} />
        </Box>
      ) : (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon fontSize="small" />}
          sx={{
            mb: 2.5,
            fontFamily: "Urbanist, sans-serif",
            fontSize: "13px",
            backgroundColor: "#FFF8EE",
            color: "#7A4F00",
            border: "1px solid #F5C97A",
            borderRadius: "8px",
            "& .MuiAlert-icon": { color: "#E6900A" },
          }}
        >
          {warningText}
        </Alert>
      )}

      <Box
        onClick={() => onConfirmChange(!confirmed)}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          p: "10px 14px",
          border: confirmed ? "1px solid #E53935" : "1px solid #E4E8ED",
          borderRadius: "8px",
          cursor: "pointer",
          backgroundColor: confirmed ? "#FFF5F5" : "#F8FAFB",
          transition: "all 0.15s",
        }}
      >
        <Checkbox
          checked={confirmed}
          onChange={(e) => onConfirmChange(e.target.checked)}
          onClick={(e) => e.stopPropagation()}
          size="small"
          sx={{
            color: "#B0BEC5",
            "&.Mui-checked": { color: "#E53935" },
            p: "2px",
            mr: 1.5,
            mt: "1px",
          }}
        />
        <Typography
          sx={{
            fontSize: "13px",
            fontFamily: "Urbanist, sans-serif",
            color: "#3F5765",
            lineHeight: 1.5,
          }}
        >
          I understand the impact of this action and wish to proceed.
        </Typography>
      </Box>
    </Box>
  );
};

// ─── Step Indicator ───────────────────────────────────────────────────────────

const StepIndicator = ({ step }) => (
  <Box sx={{ display: "flex", gap: "6px", mb: 2 }}>
    {[STEPS.SELECT, STEPS.CONFIRM].map((s) => (
      <Box
        key={s}
        sx={{
          width: s === step ? 18 : 6,
          height: 6,
          borderRadius: "3px",
          backgroundColor: s === step ? "#3299FF" : "#E4E8ED",
          transition: "width 0.2s",
        }}
      />
    ))}
  </Box>
);

// ─── Main Dialog ──────────────────────────────────────────────────────────────

export default function ApplyToAllDialog({
  open,
  onClose,
  milestoneName = "",
  milestoneId,
  onProceed,
}) {
  const [step, setStep] = useState(STEPS.SELECT);
  const [applyRosters, setApplyRosters] = useState(false);
  const [applyFrameworks, setApplyFrameworks] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [counts, setCounts] = useState({
    activeRosterCount: 0,
    activeFrameworkCount: 0,
  });
  const [countsLoading, setCountsLoading] = useState(false);

  // Reset state whenever dialog opens
  useEffect(() => {
    if (open) {
      setStep(STEPS.SELECT);
      setApplyRosters(false);
      setApplyFrameworks(false);
      setConfirmed(false);
      setCounts({ activeRosterCount: 0, activeFrameworkCount: 0 });
    }
  }, [open]);

  const handleSelectionChange = (key, value) => {
    if (key === "rosters") setApplyRosters(value);
    if (key === "frameworks") setApplyFrameworks(value);
  };

  const handleApply = async () => {
    setStep(STEPS.CONFIRM);
    setConfirmed(false);
    setCountsLoading(true);
    try {
      const response = await getBulkMilestoneCount(milestoneId);
      if (response?.statusCode === 200 && response?.data) {
        setCounts({
          activeRosterCount: response.data.ActiveClassRosterCount ?? 0,
          activeFrameworkCount: response.data.ActiveFrameworkCount ?? 0,
        });
      }
    } catch {
      // counts stay 0 if fetch fails — warning still shows
    } finally {
      setCountsLoading(false);
    }
  };

  const handleBack = () => {
    setStep(STEPS.SELECT);
    setConfirmed(false);
  };

  const handleProceed = () => {
    if (onProceed) {
      onProceed({ milestoneId, applyRosters, applyFrameworks });
    }
    onClose();
  };

  const noneSelected = !applyRosters && !applyFrameworks;

  // True when all selected targets already have the milestone (count = 0)
  const isAlreadyAssigned =
    !countsLoading &&
    (!applyRosters || counts.activeRosterCount === 0) &&
    (!applyFrameworks || counts.activeFrameworkCount === 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "10px",
          fontFamily: "Urbanist, sans-serif",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      {/* ── Title ── */}
      <DialogTitle
        sx={{
          pb: 1,
          pt: 2.5,
          px: 3,
          fontFamily: "Urbanist, sans-serif",
        }}
      >
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: "bold",
            color: "#3F5765",
            fontFamily: "Urbanist, sans-serif",
          }}
        >
          Apply Milestone to All
        </Typography>
        <Typography
          sx={{
            fontSize: "13px",
            color: "#8A9BB0",
            fontFamily: "Urbanist, sans-serif",
            mt: 0.25,
          }}
        >
          {milestoneName}
        </Typography>
      </DialogTitle>

      <Divider />

      {/* ── Content ── */}
      <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
        <StepIndicator step={step} />

        {step === STEPS.SELECT && (
          <SelectionStep
            applyRosters={applyRosters}
            applyFrameworks={applyFrameworks}
            onChange={handleSelectionChange}
          />
        )}

        {step === STEPS.CONFIRM && !isAlreadyAssigned && (
          <ConfirmStep
            milestoneName={milestoneName}
            applyRosters={applyRosters}
            applyFrameworks={applyFrameworks}
            confirmed={confirmed}
            onConfirmChange={setConfirmed}
            counts={counts}
            countsLoading={countsLoading}
          />
        )}

        {step === STEPS.CONFIRM && isAlreadyAssigned && !countsLoading && (
          <AlreadyAssignedStep
            applyRosters={applyRosters}
            applyFrameworks={applyFrameworks}
          />
        )}

        {step === STEPS.CONFIRM && countsLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} sx={{ color: "#3299FF" }} />
          </Box>
        )}
      </DialogContent>

      {/* ── Actions ── */}
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
        {step === STEPS.SELECT && (
          <>
            <Button
              onClick={onClose}
              variant="outlined"
              size="small"
              sx={{
                fontFamily: "Urbanist, sans-serif",
                textTransform: "none",
                borderColor: "#E4E8ED",
                color: "#3F5765",
                "&:hover": {
                  borderColor: "#B0BEC5",
                  backgroundColor: "#F8FAFB",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              variant="contained"
              size="small"
              disabled={noneSelected}
              sx={{
                fontFamily: "Urbanist, sans-serif",
                textTransform: "none",
                backgroundColor: "#3299FF",
                "&:hover": { backgroundColor: "#1A7FE0" },
                "&:disabled": { backgroundColor: "#E4E8ED", color: "#B0BEC5" },
              }}
            >
              Apply
            </Button>
          </>
        )}

        {step === STEPS.CONFIRM && isAlreadyAssigned && (
          <Button
            onClick={onClose}
            variant="contained"
            size="small"
            sx={{
              fontFamily: "Urbanist, sans-serif",
              textTransform: "none",
              backgroundColor: "#2AA154",
              "&:hover": { backgroundColor: "#238a45" },
            }}
          >
            OK
          </Button>
        )}

        {step === STEPS.CONFIRM && !isAlreadyAssigned && (
          <>
            <Button
              onClick={handleBack}
              variant="outlined"
              size="small"
              sx={{
                fontFamily: "Urbanist, sans-serif",
                textTransform: "none",
                borderColor: "#E4E8ED",
                color: "#3F5765",
                "&:hover": {
                  borderColor: "#B0BEC5",
                  backgroundColor: "#F8FAFB",
                },
              }}
            >
              Back
            </Button>
            <Button
              onClick={handleProceed}
              variant="contained"
              size="small"
              disabled={!confirmed}
              sx={{
                fontFamily: "Urbanist, sans-serif",
                textTransform: "none",
                backgroundColor: "#E53935",
                "&:hover": { backgroundColor: "#C62828" },
                "&:disabled": { backgroundColor: "#E4E8ED", color: "#B0BEC5" },
              }}
            >
              Proceed
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
